/**
 * 弹窗触发管理器
 * 统一管理所有弹窗的触发，支持通过 popupId 直接触发任意弹窗
 */

import { POPUP_IDS } from "../config/popupConfig";

type PopupTriggerFunction = () => void;

class PopupTriggerManager {
  private triggers: Map<string, PopupTriggerFunction> = new Map();
  private queueStatus: Map<string, boolean> = new Map();

  /**
   * 「迟到注册」补偿。
   *
   * GlobalEvent 里那批弹窗（COUNTRY_LIST / LUCKY_WHEEL_ACTIVITY /
   * APP_DOWNLOAD_REWARD_CLAIM / DOWNLOAD_APP …）挂在 App 的延迟挂载闸门之后，最长要等
   * 8s 才注册；而它们的入口（Header 国家选择器、转盘浮标、下载APP浮标、banner 动作）
   * 都在闸门之外，首屏就可见可点。这段时间里 trigger() 找不到注册者会静默 return false
   * —— 用户点了毫无反应、也没有任何提示，只能再点一次。
   *
   * 这里把「未注册时的那一次触发」记下来，等对应 popupId 注册上来立刻补触发一次，
   * 与 rulesDialogTrigger 订阅时重放当前态是同一思路。
   *
   * 两条约束防止变成惊喜弹窗：
   *   1. 只记最后一次（用户连点或点了别的入口，以最后一次为准）；
   *   2. 带 TTL，过期不补 —— 避免用户早已翻到别的页面后还突然弹出来。
   *      真实等待时长就是闸门剩余时间（≤8s），TTL 取 10s 足够覆盖且不会太久。
   * 补触发仍要过 useQueuedPopup 的 shouldShow / 路径过滤，不会绕开既有展示条件。
   */
  private pendingTrigger: { popupId: string; at: number } | null = null;
  private static readonly PENDING_TTL_MS = 10000;

  /**
   * 注册弹窗触发器
   * @param popupId 弹窗唯一标识
   * @param trigger 触发函数
   * @param isInQueue 是否已在队列中
   */
  register(
    popupId: string,
    trigger: PopupTriggerFunction,
    isInQueue: boolean
  ): void {
    // 已存在时保留首个注册者，避免多处同 popupId 注册互相覆盖
    if (!this.triggers.has(popupId)) {
      this.triggers.set(popupId, trigger);
      this.queueStatus.set(popupId, isInQueue);
      this.flushPending(popupId);
      return;
    }

    // 同一实例重复注册时仅更新队列状态
    const currentTrigger = this.triggers.get(popupId);
    if (currentTrigger === trigger) {
      this.queueStatus.set(popupId, isInQueue);
    }
  }

  /**
   * 更新弹窗队列状态
   * @param popupId 弹窗唯一标识
   * @param isInQueue 是否已在队列中
   */
  updateQueueStatus(
    popupId: string,
    isInQueue: boolean,
    trigger?: PopupTriggerFunction
  ): void {
    if (trigger) {
      const currentTrigger = this.triggers.get(popupId);
      if (currentTrigger !== trigger) {
        return;
      }
    }
    this.queueStatus.set(popupId, isInQueue);
  }

  /**
   * 取消注册弹窗触发器
   * @param popupId 弹窗唯一标识
   */
  unregister(popupId: string, trigger?: PopupTriggerFunction): void {
    if (trigger) {
      const currentTrigger = this.triggers.get(popupId);
      // 只允许当前注册者注销，避免误删其他页面的同 popupId 触发器
      if (currentTrigger !== trigger) {
        return;
      }
    }
    this.triggers.delete(popupId);
    this.queueStatus.delete(popupId);
  }

  /**
   * 触发指定弹窗
   * @param popupId 弹窗唯一标识
   * @returns 是否成功触发
   */
  trigger(popupId: string): boolean {
    const trigger = this.triggers.get(popupId);
    const isInQueue = this.queueStatus.get(popupId);

    if (!trigger) {
      // 尚未注册（多半是注册方还卡在 App 的延迟挂载闸门里）：记下来等它注册后补触发
      this.pendingTrigger = { popupId, at: Date.now() };
      return false;
    }

    if (isInQueue) {
      return false;
    }

    try {
      trigger();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 注册完成后补触发一次此前落空的点击（见 pendingTrigger 注释）。
   * 用 setTimeout(0) 推迟到注册方的 effect 跑完，避免在挂载过程中同步改它的 state。
   */
  private flushPending(popupId: string): void {
    const pending = this.pendingTrigger;
    if (!pending || pending.popupId !== popupId) return;
    this.pendingTrigger = null;
    if (Date.now() - pending.at >= PopupTriggerManager.PENDING_TTL_MS) return;
    setTimeout(() => {
      this.trigger(popupId);
    }, 0);
  }

  /**
   * 检查弹窗是否已注册
   * @param popupId 弹窗唯一标识
   */
  isRegistered(popupId: string): boolean {
    return this.triggers.has(popupId);
  }

  /**
   * 检查弹窗是否在队列中
   * @param popupId 弹窗唯一标识
   */
  isInQueue(popupId: string): boolean {
    return this.queueStatus.get(popupId) ?? false;
  }

  /**
   * 获取所有已注册的弹窗ID
   */
  getAllPopupIds(): string[] {
    return Array.from(this.triggers.keys());
  }

  /**
   * 清空所有注册
   */
  clear(): void {
    this.triggers.clear();
    this.queueStatus.clear();
  }
}

// 创建全局单例
export const popupTriggerManager = new PopupTriggerManager();

/**
 * 触发指定弹窗
 * @param popupId 弹窗唯一标识
 * @returns 是否成功触发
 */
export const triggerPopup = (popupId: string): boolean => {
  return popupTriggerManager.trigger(popupId);
};

/**
 * 等待弹窗注册完成
 * @param popupId 弹窗唯一标识
 * @param timeout 超时时间（毫秒）
 */
export const waitForPopup = (
  popupId: string,
  timeout = 5000
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (popupTriggerManager.isRegistered(popupId)) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (popupTriggerManager.isRegistered(popupId)) {
        clearInterval(checkInterval);
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error(`[PopupTriggerManager] 等待弹窗注册超时: ${popupId}`));
      }
    }, 100);
  });
};

/**
 * 触发弹窗（等待注册后再触发）
 * @param popupId 弹窗唯一标识
 * @param timeout 超时时间（毫秒）
 */
export const triggerPopupAsync = async (
  popupId: string,
  timeout = 5000
): Promise<boolean> => {
  try {
    await waitForPopup(popupId, timeout);
    return triggerPopup(popupId);
  } catch {
    return false;
  }
};

// 触发首充弹窗
export const triggerFirstRechargePopup = (): boolean =>
  triggerPopup(POPUP_IDS.FIRST_RECHARGE);

// 触发充值优惠弹窗
export const triggerRechargeOfferPopup = (): boolean =>
  triggerPopup(POPUP_IDS.RECHARGE_OFFER);

// 触发红包雨弹窗
export const triggerRedPacketPopup = (): boolean =>
  triggerPopup(POPUP_IDS.RED_PACKET);

// 触发首充弹窗
export const triggerFirstRechargePopupAsync = (
  timeout?: number
): Promise<boolean> => triggerPopupAsync(POPUP_IDS.FIRST_RECHARGE, timeout);

// 触发充值优惠弹窗
export const triggerRechargeOfferPopupAsync = (
  timeout?: number
): Promise<boolean> => triggerPopupAsync(POPUP_IDS.RECHARGE_OFFER, timeout);

// 触发红包雨弹窗
export const triggerRedPacketPopupAsync = (
  timeout?: number
): Promise<boolean> => triggerPopupAsync(POPUP_IDS.RED_PACKET, timeout);
